import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { Retry } from '@components/common';
import selectors from '@store/viewerSessions/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import commonSelector, { getFirstAppliedFilter } from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { getScanData, changeTimeLimit, changeFuncData } from '@store/viewerSessions/actions';
import { getDistributionByMetricForViewerSessions } from '@components/metrics-panel-hz/utils/metricsMapping';
import 'react-base-table/styles.css';

import SearchTool from './SearchTool';
import { filterRows } from './filterRows';
import CustomCell from './CustomCell';
import Empty from './Empty';
import { BucketLimit, TimeLimit, VM_TIME_LIMIT } from './Limit';
import { ViewerSessionsTableWrapper, ViewerSessionsTableHeader } from './styled';
import {
  sortAndSearchTable,
  replaceFinalDataKey,
  replaceColumnDataKey,
  getColumnsNameReflection,
  scanMetricFilters,
} from '../utils';
import useSetViewerSessionsExport from './useSetViewerSessionsExport';
import {
  metricFilterLookupSelector,
  metricFilterRulesSelector,
} from '@store/trends/base/selectors';
import Table from '@src/components/common/Table';

const ViewerSessionsTable = ({
  interval = { from: '', to: '' },
  filter = [],
  viewerDrillDownKey,
  metric: inputMetric = undefined,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { from, to } = interval;
  const trendsSelectedMetric = useSelector(selectedMetricSelector);
  const metric = inputMetric || trendsSelectedMetric;
  const { features } = useSelector(commonSelector);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const columnsNameReflection = getColumnsNameReflection();
  const metricFilterLookup = useSelector(metricFilterLookupSelector);

  const { columns, data, funcData, selectedMetrics, isFetching, exceedTimeLimit, errorCode } = get(
    useSelector(selectors),
    [viewerDrillDownKey],
  );

  /**
   * Below is the logic for updating the columns as part of fix for CE-8392.
   * There are certaing error dashbaords for which the ViewerSession state is in broke state.
   * The selected metrics are in format of [{metrc: 'metric1', show: true}, {metric: 'metric2', show: false}]
   * The expected format is [metric1, metric2]. This results in the error when forming the query
   * and headers are not coming up in table since the title is set to udefined in this case.
   * This is a patch up fix as we still don't know what steps actually broke the state.
   */
  columns.forEach((column) => {
    if (column.dataKey && typeof column.dataKey === 'object') {
      column.dataKey = column.dataKey.metric ? column.dataKey.metric : column.dataKey;
    }
    if (column.title === undefined) {
      column.title = columnsNameReflection[column.dataKey];
    }
  });

  useSetViewerSessionsExport({ data, columns, interval });

  const { searchText, order, orderKey } = funcData;

  // metric filter is required for some metrics
  const isMetricFilterRequired = !!getDistributionByMetricForViewerSessions(metric);
  const hasMetricFilter = metric in metricFilterLookup;

  useEffect(() => {
    const withinTimeLimit = moment(to).diff(moment(from), 'minutes') <= VM_TIME_LIMIT * 60;
    const olderThan30d = moment(from).isBefore(moment().subtract(30, 'days'));

    // sets exceeded time limit flag
    dispatch(changeTimeLimit(!withinTimeLimit || olderThan30d, viewerDrillDownKey));
  }, [from, to, viewerDrillDownKey, dispatch]);

  const fetchData = useMemo(() => {
    // add boolean rule for error metrics
    let updatedMetricFilter = metricFilter;
    if (metric in scanMetricFilters) {
      const scanDimension = scanMetricFilters[metric].dimension;
      if (!metricFilter.some((m) => m.dimension === scanDimension)) {
        updatedMetricFilter = [...metricFilter, scanMetricFilters[metric]];
      }
    }
    return () => {
      dispatch(
        getScanData({
          filter,
          from,
          to,
          name: viewerDrillDownKey,
          metric,
          metricFilter: updatedMetricFilter,
        }),
      );
    };
  }, [dispatch, filter, from, to, viewerDrillDownKey, metric, metricFilter]);

  useEffect(() => {
    fetchData();
  }, [
    fetchData,
    selectedMetrics,
    appliedFilter, // added to force reload for filter re-apply (kpi change)
  ]);

  const changeFuncDataAction = useCallback(
    (e) => {
      dispatch(changeFuncData(e, viewerDrillDownKey));
    },
    [dispatch, viewerDrillDownKey],
  );

  const headerRender = useCallback(
    ({ cells, columns: tableColumns }) =>
      tableColumns.map((item, index) => (
        <ViewerSessionsTableHeader key={item.title} title={item.title}>
          {cells[index]}
          <SearchTool
            currentKey={item.dataKey}
            changeFuncDataAction={changeFuncDataAction}
            currentSearchText={searchText[item.dataKey] || ''}
            searchText={searchText}
          />
        </ViewerSessionsTableHeader>
      )),
    [searchText, changeFuncDataAction],
  );

  const columnSort = ({ key, order: newOrder }) => {
    dispatch(
      changeFuncData(
        {
          order: newOrder,
          orderKey: key,
        },
        viewerDrillDownKey,
      ),
    );
  };

  if (exceedTimeLimit) {
    return <TimeLimit />;
  }

  if (isMetricFilterRequired && !hasMetricFilter) {
    return <BucketLimit />;
  }

  const { dataKey: columnOrderKey = '' } = columns.find((item) => item.key === orderKey) || {};

  let finalData = sortAndSearchTable('', '', order, filterRows(data, searchText), columnOrderKey);

  let enabledColumns = columns;
  if (!features[featureNames.VIEWER_DRILLDOWN_TIMELINE]) {
    enabledColumns = enabledColumns.filter((column) => column.dataKey !== 'SessionId');
  }

  // this part is tricky. dataKey can be something like 'tags.m3.dv.os'. it doesn't work!!
  // make it 'tags$m3$dv$os'
  finalData = replaceFinalDataKey(finalData);
  enabledColumns = replaceColumnDataKey(enabledColumns);

  return (
    <ViewerSessionsTableWrapper>
      <Retry isLoading={isFetching} showError={errorCode !== 0} fetchData={fetchData}>
        <Table
          style={{ height: '100%', minHeight: 600 }}
          data={finalData}
          columns={enabledColumns}
          fixed
          headerRenderer={headerRender}
          sortBy={{ key: orderKey, order }}
          onColumnSort={columnSort}
          viewerDrillDownKey={viewerDrillDownKey}
          components={{
            TableCell: CustomCell,
          }}
          emptyRenderer={<Empty />}
          {...rest}
        />
      </Retry>
    </ViewerSessionsTableWrapper>
  );
};

export default ViewerSessionsTable;
